import { useState, useEffect, useCallback } from "react";
import Snackbar from "@mui/material/Snackbar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import {
  HiCheckCircle,
  HiXCircle,
  HiExclamationCircle,
  HiInformationCircle,
  HiX,
} from "react-icons/hi";
import {
  useNotificationContext,
  useTranslate,
  undoableEventEmitter,
} from "ra-core";
import { colors, radius, shadows } from "../data/tokens";

// ------------------------------------------------------------------
// Avisos (toasts) do painel.
//
// Substitui o <Notification> padrão do react-admin, que renderiza uma
// barra preta no rodapé central. Aqui viram cartões no canto inferior
// direito, com ícone, título e descrição.
//
// A mecânica (fila, fechamento automático, e principalmente o "Desfazer"
// das exclusões reversíveis) é a mesma do componente original — mexer
// nela quebraria o desfazer, então foi replicada fielmente.
// ------------------------------------------------------------------

type NotificationType = "success" | "error" | "warning" | "info";

const ESTILOS: Record<
  NotificationType,
  { titulo: string; cor: string; Icone: React.ElementType }
> = {
  success: { titulo: "Tudo certo", cor: colors.success, Icone: HiCheckCircle },
  error: { titulo: "Algo deu errado", cor: colors.action, Icone: HiXCircle },
  warning: { titulo: "Atenção", cor: colors.warning, Icone: HiExclamationCircle },
  info: { titulo: "Informação", cor: colors.info, Icone: HiInformationCircle },
};

// O react-admin manda estas três como tipo "info", mas para quem usa o
// painel elas são confirmações — mostrar em azul de informação passa a
// impressão errada de que algo ficou pendente.
const MENSAGENS_DE_SUCESSO = [
  "ra.notification.updated",
  "ra.notification.created",
  "ra.notification.deleted",
];

export const AppNotification = (props: any) => {
  const { autoHideDuration = 4000 } = props;
  const { notifications, takeNotification } = useNotificationContext();
  const [open, setOpen] = useState(false);
  const [messageInfo, setMessageInfo] = useState<any>(undefined);
  const translate = useTranslate();

  useEffect(() => {
    if (notifications.length && !messageInfo) {
      setMessageInfo(takeNotification());
      setOpen(true);
    } else if (notifications.length && messageInfo && open) {
      // Chegou um aviso novo: fecha o atual para dar lugar a ele
      setOpen(false);
    }
  }, [notifications, messageInfo, open, takeNotification]);

  const handleRequestClose = useCallback(() => setOpen(false), []);

  const handleExited = useCallback(() => {
    if (messageInfo?.notificationOptions?.undoable) {
      undoableEventEmitter.emit("end", { isUndo: false });
    }
    setMessageInfo(undefined);
  }, [messageInfo]);

  const handleUndo = useCallback(() => {
    undoableEventEmitter.emit("end", { isUndo: true });
    setOpen(false);
  }, []);

  if (!messageInfo) return null;

  const { message, type } = messageInfo;
  const {
    autoHideDuration: autoHideDurationFromMessage,
    messageArgs,
    undoable,
  } = messageInfo.notificationOptions || {};

  const ehMensagemDeSucesso =
    typeof message === "string" && MENSAGENS_DE_SUCESSO.includes(message);
  const tipo: NotificationType = ehMensagemDeSucesso
    ? "success"
    : ((type || "info") as NotificationType);
  const { titulo, cor, Icone } = ESTILOS[tipo] ?? ESTILOS.info;

  let texto =
    typeof message === "string" ? translate(message, messageArgs) : message;

  // Erros crus de rede do navegador (API fora do ar / sem internet)
  // viram uma frase amigável — "Failed to fetch" (Chrome), "NetworkError…"
  // (Firefox) e "Load failed" (Safari) não dizem nada ao operador.
  if (
    typeof texto === "string" &&
    /^(failed to fetch|networkerror|load failed)/i.test(texto.trim())
  ) {
    texto =
      "Sem conexão com o servidor. Verifique a internet ou tente novamente em instantes.";
  }

  // Rede de segurança para o rate limit (429): o texto cru do NestJS é
  // "ThrottlerException: Too Many Requests"
  if (
    typeof texto === "string" &&
    /(throttlerexception|too many requests)/i.test(texto)
  ) {
    texto =
      "Muitas tentativas em pouco tempo. Aguarde um minuto e tente novamente.";
  }

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      autoHideDuration={autoHideDurationFromMessage || autoHideDuration}
      disableWindowBlurListener={undoable}
      TransitionProps={{ onExited: handleExited }}
      onClose={handleRequestClose}
      sx={{ maxWidth: 420 }}
    >
      <Box
        role="alert"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          width: "100%",
          minWidth: 320,
          backgroundColor: colors.white,
          borderRadius: `${radius.md}px`,
          boxShadow: shadows.popover,
          // Faixa colorida à esquerda indicando o tipo do aviso
          borderLeft: `6px solid ${cor}`,
          padding: "14px 12px 14px 16px",
        }}
      >
        <Box
          component={Icone}
          size={24}
          sx={{ color: cor, flexShrink: 0, marginTop: "1px" }}
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontWeight: 700, fontSize: 14, color: colors.textStrong }}
          >
            {titulo}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: colors.textMuted,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {texto}
          </Typography>

          {undoable && (
            <Button
              size="small"
              onClick={handleUndo}
              sx={{
                marginTop: 0.5,
                marginLeft: -1,
                fontWeight: 600,
                color: colors.primary,
              }}
            >
              {translate("ra.action.undo")}
            </Button>
          )}
        </Box>

        <IconButton
          size="small"
          aria-label="Fechar aviso"
          onClick={handleRequestClose}
          sx={{ color: colors.textMuted, flexShrink: 0 }}
        >
          <HiX size={16} />
        </IconButton>
      </Box>
    </Snackbar>
  );
};
